/**
 * FindingCardView — pure presentation of a single agent-emitted finding,
 * rendered as a Neighbours-style subgraph card on the hypothesis detail page.
 *
 * Visual layout mirrors `GetNodeNeighborhoodView` (the in-chat tool card) so a
 * finding feels like a saved snapshot of one of the agent's graph traversals:
 * header with title + subject + score, type pills + entity rows in the body,
 * "Save as investigation" + "Add to object set" footer.
 *
 * All data/IO (promote mutation, contribution wiring, navigation) is owned by
 * the connected `FindingCard` in `features/hypotheses`; this view only takes
 * resolved props + callbacks.
 *
 * The finding's `subgraph` field is populated by the
 * `list_for_run_with_subgraphs` endpoint. When it's null (e.g., during live
 * streaming before resolution) we fall back to a compact description-only
 * layout.
 */

import {
  BuildingIcon,
  CreditCardIcon,
  FileTextIcon,
  Loader2,
  MapPinIcon,
  NetworkIcon,
  Plus,
  UserIcon,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { ToolActionHandlers } from '@/components/agent/tools/_shared/chat-tool-actions'
import { FindingEvidence } from '@/components/evidence/EvidenceReferenceButton'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import type { ToolCardActionsModel } from '@/features/agent/tools/useToolCardActions'
import type { FindingEntity, HypothesisFinding } from '@/types/hypothesis-finding'
import { cn } from '@/utils/cn'
import { FindingActions, PromoteHint } from './FindingActions'
import { SourceFindingCard } from './SourceFindingCard'

export interface FindingCardViewProps {
  finding: HypothesisFinding
  caseId: string
  liveBadge?: boolean
  /** 0-based position in the run; rendered as an `F{n}` badge so the card
   * matches the report's [F#] citations. */
  index?: number
  /** Flash the card when a report [F#] citation targets it. */
  highlighted?: boolean
  /** Promote-to-investigation in flight. */
  isPromoting: boolean
  /** Promote the finding to a saved Investigation. */
  onPromote: () => void
  /** Graph-action handlers (show on graph / add to object set). Empty object
   *  when the finding has no resolvable graph view. */
  handlers: ToolActionHandlers
  /** Resolved actions model for the shared `ToolCardActions` footer, or null
   *  when there are no graph actions to show. */
  actions: ToolCardActionsModel | null
  /** Optional hint text rendered before the graph actions. */
  hint?: string | null
}

const LABEL_ICON: Record<string, typeof UserIcon> = {
  Person: UserIcon,
  Company: BuildingIcon,
  BankAccount: CreditCardIcon,
  Address: MapPinIcon,
  Document: FileTextIcon,
}

function primaryLabel(labels: string[]): string | undefined {
  return labels.filter(l => !l.startsWith('__') && l !== 'Node')[0]
}

export function FindingCardView({
  finding,
  caseId,
  liveBadge,
  index,
  highlighted,
  isPromoting,
  onPromote,
  handlers,
  actions,
}: FindingCardViewProps) {
  const { t } = useTranslation()
  const subgraph = finding.subgraph
  const promotion = { isPending: isPromoting, onPromote }
  const badge = index === undefined ? null : <FindingBadge label={`F${index + 1}`} />
  // Flash when a report [F#] citation targets this card.
  const highlightCls = highlighted ? 'ring-2 ring-primary' : undefined

  const groupEntries = subgraph
    ? Object.entries(subgraph.entitiesByLabel).sort((a, b) => b[1].length - a[1].length)
    : []

  const [activeLabel, setActiveLabel] = useState<string | null>(null)
  // Auto-select the largest group on mount.
  useEffect(() => {
    if (activeLabel === null && groupEntries.length > 0) {
      setActiveLabel(groupEntries[0][0])
    }
  }, [groupEntries, activeLabel])

  const activeEntries: FindingEntity[] =
    activeLabel && subgraph?.entitiesByLabel[activeLabel]
      ? subgraph.entitiesByLabel[activeLabel]
      : []

  if (finding.entityIds.length === 0 && finding.evidenceReferenceIds?.length) {
    return (
      <SourceFindingCard
        finding={finding}
        caseId={caseId}
        presentation={{ index, highlighted }}
        promotion={promotion}
      />
    )
  }

  // Fallback view when subgraph isn't resolved (live SSE before refetch, or
  // a finding with empty entity_ids).
  if (!subgraph || subgraph.entityCount === 0) {
    return (
      <Card size="compact" className={cn('flex flex-col gap-3 p-4 md:p-5', highlightCls)}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-start gap-2 md:items-center">
            {badge}
            <h3 className="min-w-0 break-words text-base font-semibold leading-tight tracking-tight">
              {finding.title}
            </h3>
          </div>
          {liveBadge && <LiveChip />}
        </div>
        {/* Clamped from md only: at ~275 px four lines hide most of the finding
            and there is no other place to read it. */}
        <p className="text-sm leading-relaxed text-muted-foreground max-md:break-words md:line-clamp-4">
          {finding.description}
        </p>
        {finding.evidenceSummary && (
          <p className="text-xs italic text-muted-foreground/80 max-md:break-words md:line-clamp-2">
            {finding.evidenceSummary}
          </p>
        )}
        <FindingActions
          caseId={caseId}
          investigationId={finding.promotedInvestigationId}
          promotion={promotion}
        />
      </Card>
    )
  }

  return (
    <Card
      size="compact"
      className={cn('flex flex-col overflow-hidden p-0 data-[size=compact]:py-0', highlightCls)}
    >
      {/* Header */}
      <header className="flex items-start justify-between gap-3 border-b border-border px-3 py-3 md:px-4">
        <div className="flex min-w-0 items-start gap-2 md:items-center">
          {badge}
          {/* Below md the title wraps: one line holds only ~25 characters. */}
          <h3 className="min-w-0 text-sm font-semibold leading-tight tracking-tight text-foreground max-md:break-words md:truncate">
            {finding.title}
          </h3>
        </div>
        {liveBadge && <LiveChip compact />}
      </header>

      {/* Body */}
      <div className="flex flex-col gap-4 px-3 py-4 md:px-4">
        <div>
          <p className="break-words text-sm leading-relaxed text-foreground">
            {finding.description}
          </p>
          {finding.evidenceSummary && (
            <p className="mt-2 break-words rounded-md border border-border/60 bg-muted/30 px-3 py-2 text-xs italic text-muted-foreground">
              {finding.evidenceSummary}
            </p>
          )}
        </div>

        <FindingEvidence caseId={caseId} references={finding.evidenceReferenceIds} />
        <div className="grid grid-cols-1 gap-4 md:grid-cols-[minmax(160px,200px)_1fr]">
          {/* Chips column */}
          <div className="flex flex-row flex-wrap gap-1.5 md:flex-col md:flex-nowrap">
            {groupEntries.map(([label, entities]) => {
              const Icon = LABEL_ICON[label] ?? UserIcon
              const isActive = label === activeLabel
              return (
                <button
                  key={label}
                  type="button"
                  onClick={() => setActiveLabel(label)}
                  className={cn(
                    'inline-flex items-center justify-between gap-2 rounded-full border px-3 py-2 text-[11px] transition md:px-2.5 md:py-1',
                    isActive
                      ? 'border-primary/60 bg-primary/10 text-primary'
                      : 'border-border bg-background text-muted-foreground hover:bg-muted'
                  )}
                >
                  <span className="inline-flex items-center gap-1.5">
                    <Icon className="size-3" />
                    {label}
                  </span>
                  <span
                    className={cn(
                      'rounded px-1 text-[10px] font-semibold',
                      isActive ? 'bg-primary/20 text-primary' : 'bg-muted text-muted-foreground'
                    )}
                  >
                    {entities.length}
                  </span>
                </button>
              )
            })}
          </div>

          {/* Entity list */}
          <ul className="flex min-w-0 flex-col divide-y divide-border rounded-md border border-border">
            {activeEntries.map(n => {
              const label = primaryLabel(n.labels)
              return (
                <li
                  key={n.elementId}
                  className="grid min-w-0 grid-cols-[auto_minmax(0,1fr)_auto] items-start gap-x-2.5 gap-y-0.5 px-3 py-2 text-xs md:flex"
                >
                  <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-primary" />
                  {/* Below md the row is a grid (name + label badge on the first
                      line, subtitle under them at full width). The subtitle is
                      often the only identifying text ("Unknown" names), so it
                      wraps instead of being cut after ~20 characters. From md
                      it is a flex row, where the col-/row- classes do nothing. */}
                  <div className="contents min-w-0 flex-1 md:block">
                    <p className="col-start-2 row-start-1 font-medium text-foreground max-md:break-words md:truncate">
                      {n.name}
                    </p>
                    {n.subtitle && (
                      <p className="col-span-2 col-start-2 row-start-2 text-[11px] text-muted-foreground max-md:line-clamp-4 max-md:break-words md:truncate">
                        {n.subtitle}
                      </p>
                    )}
                  </div>
                  {label && (
                    <span className="col-start-3 row-start-1 shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                      {label}
                    </span>
                  )}
                </li>
              )
            })}
          </ul>
        </div>
      </div>

      {/* Footer — graph action + save, left-aligned with the standard button. */}
      <footer className="flex flex-wrap items-center gap-2 border-t border-border bg-muted/20 px-3 py-3 md:px-4">
        {actions && handlers[actions.primary] && (
          <Button size="xs" className="gap-1.5 max-md:h-9" onClick={handlers[actions.primary]}>
            {actions.primary === 'addToGraph' ? (
              <Plus className="size-3.5" />
            ) : (
              <NetworkIcon className="size-3.5" />
            )}
            {t(`chat.toolActions.${actions.primary}`)}
          </Button>
        )}
        <PromoteHint
          caseId={caseId}
          investigationId={finding.promotedInvestigationId}
          promotion={promotion}
        />
      </footer>
    </Card>
  )
}

function FindingBadge({ label }: { label: string }) {
  return (
    <span className="inline-flex shrink-0 items-center rounded bg-primary/10 px-1.5 py-0.5 font-mono text-[11px] font-medium text-primary">
      {label}
    </span>
  )
}

function LiveChip({ compact }: { compact?: boolean } = {}) {
  const { t } = useTranslation()
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full border border-primary/40 bg-primary/10 font-semibold uppercase tracking-wider text-primary',
        compact ? 'px-1.5 py-0.5 text-[9px]' : 'px-2 py-0.5 text-[10px]'
      )}
    >
      <Loader2 className={cn('animate-spin', compact ? 'size-2' : 'size-2.5')} />
      {t('search.detail.finding.live')}
    </span>
  )
}
